import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Link } from '@fluentui/react-components';
import { ChevronRight16Regular } from '@fluentui/react-icons';
import { RouteConfig } from '#root/routes/config';
import { routes } from '#root/routes/config';
import './styles.scss';

type ModuleContext = 'vadmin' | 'vtracker';

interface BreadcrumbsProps {
  currentPageName?: string;
}

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ currentPageName }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const getContext = (path: string): ModuleContext | undefined => {
    const firstSegment = path.split('/')[1];
    if (firstSegment === 'vadmin' || firstSegment === 'vtracker') {
      return firstSegment;
    }
    return undefined;
  };

  const matchRoute = (routePath: string, currentPath: string): boolean => {
    const routeParts = routePath.split('/');
    const currentParts = currentPath.split('/');

    if (routeParts.length !== currentParts.length) return false;

    return routeParts.every((part, i) => {
      // If it's a parameter (starts with :), it matches anything
      if (part.startsWith(':')) return true;
      return part === currentParts[i];
    });
  };

  const findRouteWithParents = (currentPath: string, routeList: RouteConfig[], parentPath = ''): RouteConfig[] => {
    for (const route of routeList) {
      const fullPath = route.path.startsWith('/') ? route.path : `${parentPath}/${route.path}`;
      
      if (matchRoute(fullPath, currentPath)) {
        return [route];
      }

      if (route.children) {
        const childResult = findRouteWithParents(currentPath, route.children, fullPath);
        if (childResult.length > 0) {
          return [route, ...childResult];
        }
      }
    }
    return [];
  };

  const getBreadcrumbs = () => {
    const context = getContext(location.pathname);
    const contextRoutes = context 
      ? routes.filter(r => r.path === `/${context}`)
      : routes;

    const routeChain = findRouteWithParents(location.pathname, contextRoutes);
    let currentPath = '';
    
    return routeChain.map((route, index) => {
      // Build the path by replacing parameters with actual values
      const routeParts = route.path.split('/');
      const locationParts = location.pathname.split('/');
      const pathParts = routeParts.map((part, i) => {
        if (part.startsWith(':')) {
          return locationParts[i + (currentPath.split('/').length - 1)];
        }
        return part;
      });
      
      currentPath = pathParts.join('/');
      const isLast = index === routeChain.length - 1;
      const label = isLast && currentPageName ? currentPageName : route.title;

      return {
        path: currentPath,
        label,
        isLast
      };
    });
  };

  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length === 0) {
    return null;
  }

  return (
    <div className="emc-breadcrumbs">
      {breadcrumbs.map((crumb, index) => (
        <React.Fragment key={crumb.path}>
          {crumb.isLast ? (
            <span className="emc-breadcrumbs__current">{crumb.label}</span>
          ) : (
            <>
              <Link
                className="emc-breadcrumbs__link"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(crumb.path);
                }}
              >
                {crumb.label}
              </Link>
              <ChevronRight16Regular />
            </>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};
